import * as constants from '@/store/modules/listsDashboards/lists-dashboards-types';
import { ListsDashboardSourceEnum } from '@/bundles/App/enums';
import { Nullable } from '@/utils/types';
import {
  IListsDashboardsSourcePayload,
  IDealsPipelineFiltersUi,
  IDealsPipelineFilterOptions,
  IDealsPipelineSliceBase,
  DashboardViewEnum,
} from '@/bundles/DashboardAnalytics/types';
import {
  IListsDashboardsState,
  IPipelineDashboardFilters,
  IPipelineDashboardToggleFilterValuePayload,
  PipelineMeasureEnum,
  PipelineKanbanSortEnum,
} from '@/bundles/Pipeline/types';
import { DealsMeasureEnum } from '@/bundles/Deals/types';
import {
  AvailabilitiesDistributionSortEnum,
  IAvailabilitiesDashboardFilters,
  IAvailabilitiesDashboardToggleFilterValuePayload,
} from '@/bundles/Availabilities/types';
import {
  IComparablesDashboardFilters,
  IComparablesDashboardSlice,
  IComparablesDashboardToggleFilterValuePayload,
  IComparablesSetSidePayload,
  IComparablesSetComparePayload,
  IComparablesSetCombinePayload,
} from '@/bundles/Comparables/types';
import { MAX_SELECTED_YEARS, MIN_SELECTED_YEARS } from '@/bundles/DashboardAnalytics/helpers/yearFilterConsts';
import { SLICE_FACTORIES } from '@/store/modules/listsDashboards/helpers';

type DealsPipelineSlice = IDealsPipelineSliceBase<IDealsPipelineFiltersUi, string>;

function sliceOf<K extends ListsDashboardSourceEnum> (state: IListsDashboardsState, source: K): IListsDashboardsState[K] {
  return state[source];
}

// filterOptions / activeMeasure / view exist only on the deals & pipeline slices, never on
// availabilities — and these mutations are never dispatched for that source.
function dealsPipelineSliceOf (state: IListsDashboardsState, source: ListsDashboardSourceEnum): DealsPipelineSlice {
  return state[source] as unknown as DealsPipelineSlice;
}

// `ui` exists only on the comparables slice; the SET_* client-toggle mutations below are
// never dispatched for another source. Resolved through sliceOf(state, source) — never a
// hardcoded slice — so it honours payload.source like SET_FILTERS does.
function comparablesSliceOf (state: IListsDashboardsState, source: ListsDashboardSourceEnum): IComparablesDashboardSlice {
  return sliceOf(state, source) as unknown as IComparablesDashboardSlice;
}

function resetSlice<K extends ListsDashboardSourceEnum> (state: IListsDashboardsState, source: K): void {
  state[source] = SLICE_FACTORIES[source]();
}

// An empty year reads as "all years", which only a saved set opens on — so `allowEmpty` is the set flag.
function clampYear (next: number[] | undefined, current: number[], allowEmpty: boolean, max: number): number[] {
  if (!Array.isArray(next)) {
    return current;
  }
  const unique = Array.from(new Set(next.filter(item => Number.isFinite(item))));
  if (unique.length < MIN_SELECTED_YEARS && !allowEmpty) {
    return current;
  }
  if (unique.length > max) {
    return unique.slice(0, max);
  }
  return unique;
}

export default {
  [constants.SET_FILTERS] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<Partial<IPipelineDashboardFilters> | Partial<IComparablesDashboardFilters>>) {
    const slice = sliceOf(state, source);
    // Partial keeps the `'year' in current` check conditional — availabilities has no `year`.
    const current = slice.filters as Partial<IDealsPipelineFiltersUi>;
    if ('year' in current) {
      const { year, ...rest } = payload;
      const filters = { ...current, ...rest } as IDealsPipelineFiltersUi;
      // Only the deals dashboard opens a saved set on all years.
      const allowEmpty = source === ListsDashboardSourceEnum.deals && Boolean(filters.setId);
      // The pipeline dashboard has no year limit.
      const maxYears = source === ListsDashboardSourceEnum.pipeline ? Number.POSITIVE_INFINITY : MAX_SELECTED_YEARS;
      const nextYear = 'year' in payload ? clampYear(year, current.year as number[], allowEmpty, maxYears) : current.year as number[];
      (slice as { filters: IDealsPipelineFiltersUi }).filters = { ...filters, year: nextYear };
    } else {
      (slice as { filters: IAvailabilitiesDashboardFilters }).filters =
        { ...current, ...payload } as unknown as IAvailabilitiesDashboardFilters;
    }
  },
  [constants.TOGGLE_FILTER_VALUE] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<IPipelineDashboardToggleFilterValuePayload | IAvailabilitiesDashboardToggleFilterValuePayload | IComparablesDashboardToggleFilterValuePayload>) {
    const slice = sliceOf(state, source);
    const filters = slice.filters as Record<string, string[]>;
    const current = filters[payload.key] ?? [];
    const next = current.includes(payload.value)
      ? current.filter(item => item !== payload.value)
      : [...current, payload.value];

    (slice as { filters: Record<string, string[]> }).filters = { ...filters, [payload.key]: next };
  },
  [constants.RESET_FILTERS] (state: IListsDashboardsState, { source }: { source: ListsDashboardSourceEnum }) {
    (sliceOf(state, source) as { filters: unknown }).filters = SLICE_FACTORIES[source]().filters;
  },
  [constants.SET_FILTER_OPTIONS_LOADING] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<boolean>) {
    dealsPipelineSliceOf(state, source).filterOptions.loading = payload;
  },
  [constants.SET_FILTER_OPTIONS_DATA] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<IDealsPipelineFilterOptions>) {
    const { filterOptions } = dealsPipelineSliceOf(state, source);
    filterOptions.data = payload;
    filterOptions.error = null;
  },
  [constants.SET_FILTER_OPTIONS_ERROR] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<Nullable<string>>) {
    dealsPipelineSliceOf(state, source).filterOptions.error = payload;
  },
  [constants.SET_ACTIVE_MEASURE] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<PipelineMeasureEnum | DealsMeasureEnum>) {
    dealsPipelineSliceOf(state, source).activeMeasure = payload;
  },
  [constants.SET_KANBAN_SORT] (state: IListsDashboardsState, { payload }: IListsDashboardsSourcePayload<PipelineKanbanSortEnum>) {
    state[ListsDashboardSourceEnum.pipeline].kanbanSort = payload;
  },
  [constants.SET_DISTRIBUTION_SORT] (state: IListsDashboardsState, { payload }: IListsDashboardsSourcePayload<AvailabilitiesDistributionSortEnum>) {
    state[ListsDashboardSourceEnum.availabilities].distributionSort = payload;
  },
  [constants.SET_SORT_METRIC] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<string>) {
    comparablesSliceOf(state, source).ui.sortMetric = payload;
  },
  [constants.SET_SIDE] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<IComparablesSetSidePayload>) {
    comparablesSliceOf(state, source).ui[payload.key] = payload.value;
  },
  [constants.SET_COMPARE] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<IComparablesSetComparePayload>) {
    comparablesSliceOf(state, source).ui[payload.key] = payload.value;
  },
  [constants.SET_COMBINE] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<IComparablesSetCombinePayload>) {
    comparablesSliceOf(state, source).ui[payload.key] = payload.value;
  },
  [constants.RESET_ALL] (state: IListsDashboardsState) {
    Object.values(ListsDashboardSourceEnum).forEach(source => resetSlice(state, source));
  },
  [constants.SET_VIEW] (state: IListsDashboardsState, { source, payload }: IListsDashboardsSourcePayload<DashboardViewEnum>) {
    dealsPipelineSliceOf(state, source).view = payload;
  },
};
